import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnDestroy
} from '@angular/core';
import { Subscription, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, take } from 'rxjs/operators';
import { Router } from '@angular/router';

import { isNullOrUndefined } from '@app/util/util';
import { ExchangeBookPayload } from '@user-history/entities/user-history-item';
import { FlightResultsFacade } from '@flight-results/flight-results.facade';
import { NewFlightItemVm } from '@app/entities/flight-results';
import { ANIMATIONS, TIME_TO_SLIDE } from '@app/constants/animations.constants';
import { AppFacade } from '@app/app.facade';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { ReservationType } from '@user-history/entities/user-history-item';
import { userHistoryRoute, userHistoryDetailRoute } from '@user-history/constants/history-routes.constants';
import { isActiveMobileInterface } from '@app/modules/core/mappers/mobile-interface.mapper';
import { ButtonType } from '@design-system/components/molecules/button/entities/button.types';
import { goToSupportHelper } from '@app/helpers/open-url.helper';

@Component({
  selector: 'app-flight-confirm-exchange',
  templateUrl: './flight-confirm-exchange.container.html',
  styleUrls: ['./flight-confirm-exchange.container.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FlightConfirmExchangeContainer implements OnInit, OnDestroy {

  public animation: string = ANIMATIONS.SLIDE_IN_BOTTOM;
  public infoAnimation: string = ANIMATIONS.SLIDE_IN_RIGHT;
  public termsAnimation: string = ANIMATIONS.SLIDE_IN_RIGHT;
  public supportImg: string = 'ilustra-soporte.png';
  public fadeOut: boolean = false;
  public showLoaderSuccessOrError: boolean = false;
  public isLoadingExchangeDelay: boolean = false;
  public hiddenConfirmation: boolean = false;
  public showTerms: boolean = false;

  public exchangePayload: ExchangeBookPayload;
  public selectedFlights: NewFlightItemVm[];
  public freeOfCharge: boolean;
  public isMobile: boolean;

  public readonly cancelButtonType = ButtonType.Secondary;

  private isLoadingExchangeDelay$: Subscription;
  private delay: number = TIME_TO_SLIDE;
  private subscriptions: Subscription[] = [];
  private exchangeFlightWithAssistance: boolean = false;

  constructor(
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef,
    private domManipulateService: DomManipulateService,
    private facade: FlightResultsFacade,
    private appFacade: AppFacade
  ) {
    const state = this.router.getCurrentNavigation();
    if (state && state?.extras && state?.extras?.state) {
      const {
        exchangePayload, selectedFlights, freeOfCharge, isMobile, exchangeFlightWithAssistance
      } = this.router.getCurrentNavigation()?.extras?.state;
      this.exchangePayload = exchangePayload;
      this.selectedFlights = selectedFlights;
      this.freeOfCharge = !!freeOfCharge;
      this.isMobile = !!isMobile;
      this.exchangeFlightWithAssistance = !!exchangeFlightWithAssistance;
    }
  }

  ngOnInit(): void {
    this.domManipulateService.setBodyScroll(false);
    this.subscriptions.push(
      this.successExchange$.pipe(distinctUntilChanged()).subscribe(successExchange => {
        if (successExchange && this.showLoaderSuccessOrError && this.withCheckoutInterface) {
          setTimeout(() => this.continueAction(), TIME_TO_SLIDE);
        }
      })
    );
    this.isLoadingExchangeDelay$ = combineLatest(
      [this.isLoadingExchange$.pipe(distinctUntilChanged()),
        this.errorInExchangeBook$.pipe(distinctUntilChanged())]
    ).subscribe(([isLoadingExchange, errorInExchangeBook]) => {
      if (errorInExchangeBook) {
        setTimeout(() => {
          this.isLoadingExchangeDelay = false;
          this.changeDetectorRef.detectChanges();
        }, this.delay);
      } else {
        if (isLoadingExchange) {
          setTimeout(() => {
            this.hiddenConfirmation = true;
            this.changeDetectorRef.detectChanges();
          }, this.delay);
        }
        this.isLoadingExchangeDelay = isLoadingExchange;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  ngOnDestroy(): void {
    if (!isNullOrUndefined(this.isLoadingExchangeDelay$)) {
      this.isLoadingExchangeDelay$.unsubscribe();
    }
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  get isLoadingExchange$(): Observable<boolean> {
    return this.facade.isLoadingExchange$;
  }

  get errorInExchangeBook$(): Observable<boolean> {
    return this.facade.errorInExchangeBook$;
  }

  get withCostIsLoadingExchangeDelay$(): Observable<boolean> {
    return this.errorInExchangeBook$.pipe(map(errorInExchangeBook => this.isLoadingExchangeDelay || !errorInExchangeBook));
  }

  get successExchange$(): Observable<boolean> {
    return this.facade.successExchange$;
  }

  get withCheckoutInterface(): boolean {
    return isActiveMobileInterface() && !this.freeOfCharge;
  }

  public closeAction(): void {
    this.animation = ANIMATIONS.SLIDE_OUT_BOTTOM;
    this.infoAnimation = ANIMATIONS.SLIDE_OUT_RIGHT;
    this.fadeOut = true;
    setTimeout(() => this.appFacade.navigateToLastRoute(), TIME_TO_SLIDE);
    this.domManipulateService.setBodyScroll(true);
  }

  public exchangeBook(): void {
    this.showLoaderSuccessOrError = true;
    this.facade.exchangeBook(this.exchangePayload, this.selectedFlights,
      this.freeOfCharge, this.isMobile, this.exchangeFlightWithAssistance);
  }

  public goToSupportLink(): void {
    goToSupportHelper();
  }

  public continueAction(): void {
    if (this.freeOfCharge) this.goToHistory();
    else this.closeAction();
  }

  public closeTerms(): void {
    this.termsAnimation = ANIMATIONS.SLIDE_OUT_RIGHT;
    setTimeout(() => this.showTerms = false, TIME_TO_SLIDE);
  }

  public openTerms(): void {
    this.termsAnimation = ANIMATIONS.SLIDE_IN_RIGHT;
    this.showTerms = true;
  }

  private goToHistory(): void {
    this.appFacade.user$.pipe(take(1)).subscribe(user => {
      this.domManipulateService.setBodyScroll(false);
      this.router.navigate([userHistoryRoute, userHistoryDetailRoute,
        user.id, this.exchangePayload.TravelItineraryId, ReservationType.Flight]);
    }).unsubscribe();
  }

}
